/**
 * Bulk list selection for the redesigned Manage Returns page: explicit included ids vs
 * filter-all with exclusions. No filter snapshot — committed filters are read at submit
 * from the injected filters store.
 */
import { makeAutoObservable } from "mobx";
import { TRISTATE_CHECKBOX_VALUES } from "../../../../components/inputs/tristate-checkbox/tristate-checkbox";
import {
    IBulkSelectionFilters,
    IStartBulkActionPayload,
} from "../../../../services/api/bulk-rma-action-types";
import RmaListFiltersStore from "../../rma-list-page/stores/rma-list-filters-store";

export enum BULK_SELECTION_MODE {
    NONE = "none",
    EXPLICIT = "explicit",
    FILTER_ALL = "filter_all",
}

class RmaBulkSelectionStore {
    private _mode: BULK_SELECTION_MODE = BULK_SELECTION_MODE.NONE;
    private _includedIds = new Set<number>();
    private _excludedIds = new Set<number>();
    private _isBlocked = false;

    constructor(private readonly _filtersStore: RmaListFiltersStore) {
        makeAutoObservable(this, {}, { autoBind: true });
    }

    public get mode(): BULK_SELECTION_MODE {
        return this._mode;
    }

    public get isBlocked(): boolean {
        return this._isBlocked;
    }

    public setBlocked(blocked: boolean): void {
        this._isBlocked = blocked;
        if (blocked) {
            this.clear();
        }
    }

    public selectionCount(listTotal: number): number {
        if (this._mode === BULK_SELECTION_MODE.EXPLICIT) {
            return this._includedIds.size;
        }
        if (this._mode === BULK_SELECTION_MODE.FILTER_ALL) {
            return Math.max(0, listTotal - this._excludedIds.size);
        }
        return 0;
    }

    public headerState(): TRISTATE_CHECKBOX_VALUES {
        if (this._mode === BULK_SELECTION_MODE.NONE) {
            return TRISTATE_CHECKBOX_VALUES.UNCHECKED;
        }
        if (
            this._mode === BULK_SELECTION_MODE.FILTER_ALL &&
            this._excludedIds.size === 0
        ) {
            return TRISTATE_CHECKBOX_VALUES.CHECKED;
        }
        if (
            this._mode === BULK_SELECTION_MODE.FILTER_ALL &&
            this._excludedIds.size > 0
        ) {
            return TRISTATE_CHECKBOX_VALUES.PARTIALLY_CHECKED;
        }
        if (
            this._mode === BULK_SELECTION_MODE.EXPLICIT &&
            this._includedIds.size > 0
        ) {
            return TRISTATE_CHECKBOX_VALUES.PARTIALLY_CHECKED;
        }
        return TRISTATE_CHECKBOX_VALUES.UNCHECKED;
    }

    public isRmaSelected(rmaId: number): boolean {
        if (this._mode === BULK_SELECTION_MODE.FILTER_ALL) {
            return !this._excludedIds.has(rmaId);
        }
        if (this._mode === BULK_SELECTION_MODE.EXPLICIT) {
            return this._includedIds.has(rmaId);
        }
        return false;
    }

    /** Reset to unselected (none); used after filter scope commits, confirm, navigate away, block. */
    public clear(): void {
        this._mode = BULK_SELECTION_MODE.NONE;
        this._includedIds = new Set();
        this._excludedIds = new Set();
    }

    /** Header select-all on: entire filtered set. */
    public selectAllMatchingFilters(): void {
        if (this._isBlocked) {
            return;
        }
        this._mode = BULK_SELECTION_MODE.FILTER_ALL;
        this._includedIds = new Set();
        this._excludedIds = new Set();
    }

    public toggleRma(rmaId: number): void {
        if (this._isBlocked) {
            return;
        }

        if (this._mode === BULK_SELECTION_MODE.FILTER_ALL) {
            if (this._excludedIds.has(rmaId)) {
                this._excludedIds.delete(rmaId);
            } else {
                this._excludedIds.add(rmaId);
            }

            // Copying the object, because MobX doesn't react to changes in the set.
            this._excludedIds = new Set(this._excludedIds);
            return;
        }

        if (this._includedIds.has(rmaId)) {
            this._includedIds.delete(rmaId);
            if (this._includedIds.size === 0) {
                this._mode = BULK_SELECTION_MODE.NONE;
            }
        } else {
            this._includedIds.add(rmaId);
            this._mode = BULK_SELECTION_MODE.EXPLICIT;
        }
        this._includedIds = new Set(this._includedIds);
    }

    public buildStartPayload(): IStartBulkActionPayload | null {
        if (
            this._mode === BULK_SELECTION_MODE.EXPLICIT &&
            this._includedIds.size > 0
        ) {
            return { rma_ids: Array.from(this._includedIds) };
        }
        if (this._mode === BULK_SELECTION_MODE.FILTER_ALL) {
            return {
                filters: this._collectSelectionFilters(),
                excluded_rma_ids: Array.from(this._excludedIds),
            };
        }
        return null;
    }

    /**
     * Allow-listed filters for filter-scoped bulk start (API contract; no page/sort).
     * Reads committed list filters only — sidebar drafts must not ride along.
     */
    private _collectSelectionFilters(): IBulkSelectionFilters {
        const applied = this._filtersStore.getAppliedFilters();
        return {
            search_input: applied.search_input,
            search_exact_match: applied.search_exact_match,
            filter_resolution_id: applied.filter_resolution_id,
            filter_return_reason_id: applied.filter_return_reason_id,
            filter_restock_validated_type:
                applied.filter_restock_validated_type,
            filter_date_range: applied.filter_date_range || null,
            filter_status: applied.filter_status,
            filter_reserve_item_expiration:
                applied.filter_reserve_item_expiration,
            filter_reserve_item_expiration_input:
                applied.filter_reserve_item_expiration_input,
            filter_payment_status_type: applied.filter_payment_status_type,
            filter_manual_actions: [...applied.filter_manual_actions],
            filter_error_labels: [...applied.filter_error_labels],
            filter_on_track: [...applied.filter_on_track],
            status_group: applied.status_group,
        };
    }
}

export { RmaBulkSelectionStore };
